import { provideHttpClient } from '@angular/common/http';
import { enableProdMode, provideZonelessChangeDetection } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideRouter } from '@angular/router';
import { provideTransloco, translocoConfig } from '@ngneat/transloco';
import { provideTanStackQuery, QueryClient, withDevtools } from '@tanstack/angular-query-experimental';
import { defineCustomElement as defineJuiIcon } from '@jump-tech-frontend/core-components/components/jui-icon.js';
import { defineCustomElement as defineJuiSkeletonLoader } from '@jump-tech-frontend/core-components/components/jui-skeleton-loader.js';
import { environment } from './environments/environment';
import { AppComponent } from './app/app.component';
import { appRoutes } from './app/app.routes';
import { PortalTranslocoLoader } from './app/transloco-loader';

if (environment.production) {
  enableProdMode();
}

// Feed the dev-only BFF base url into the lazily-loaded portal lib before bootstrap. The lib
// reads this off globalThis (see PORTAL_BFF_BASE_URL); it cannot be a DI provider here without
// a static import that would break lazy loading. Only `local` sets bffBaseUrl, so the data
// layer serves the shared stubs everywhere else (ADR-0019).
(globalThis as { __portalBffBaseUrl__?: string }).__portalBffBaseUrl__ = environment.bffBaseUrl;

// Same mechanism for the lazily-loaded survey chunk (portal-relay-host): it builds the
// embedded relay's environment from this when the survey route is entered.
(globalThis as { __portalRelayEnvironment__?: { name: string; production: boolean } }).__portalRelayEnvironment__ = {
  name: environment.relayEnvironmentName,
  production: environment.production
};

// Register the core-components custom elements we use. The Angular proxies do not
// self-register in this repo, and the lazy /loader does not bundle cleanly under esbuild,
// so register per component via dist-custom-elements. See ADR-0007.
defineJuiIcon();
defineJuiSkeletonLoader();

bootstrapApplication(AppComponent, {
  providers: [
    provideZonelessChangeDetection(),
    provideRouter(appRoutes),
    provideHttpClient(),
    provideTanStackQuery(
      new QueryClient({
        defaultOptions: { queries: { retry: 2, staleTime: 30_000, gcTime: 5 * 60_000 } }
      }),
      withDevtools()
    ),
    provideTransloco({
      config: translocoConfig({
        availableLangs: ['en'],
        defaultLang: 'en',
        reRenderOnLangChange: true,
        prodMode: environment.production,
        missingHandler: { useFallbackTranslation: true }
      }),
      loader: PortalTranslocoLoader
    })
  ]
})
  .then(() => {
    // Warm the embedded survey's deferred chunk once the browser is idle, so the home
    // CTA click skips the download (see prefetchEmbeddedRelay for the trade-offs).
    const requestIdle: (callback: () => void) => unknown =
      'requestIdleCallback' in window ? window.requestIdleCallback.bind(window) : cb => setTimeout(cb, 2000);
    requestIdle(() => {
      void import('@jump-tech-frontend/portal-relay-host').then(m => m.prefetchEmbeddedRelay());
    });
  })
  .catch(console.error);
